import $ from 'jquery';
import { fabric } from 'fabric';
import 'fabric/src/mixins/eraser_brush.mixin';
import '~src/filters/exposure'; // Register custom exposure filter
import '~src/filters/shadowBoost'; // Register custom shadow boost filter
import {
  EDITOR_MODE_DEFAULT,
  EDITOR_MODE_ORG_TEMPLATE_MANAGER,
  EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER,
} from '~config/editorConstants';
import { loadFonts } from '~core/text';
import {
  displayHeaderControls,
  fixTooltipIssueOnClick,
  initColors,
  injectSvgIcons,
  setHeaderSummary,
} from '../ui';
import { initSidebar } from '../sidebarLegacy';
import { startKeyboardListener } from '../events';
import { TextTemplate } from '@onoranzefunebricloud/giotto-text-template';
import {
  addPagesToSidebar,
  generateCurrentPageThumbnail,
  addPage,
} from '~src/multipages';
import { createDropzoneLogo } from '../upload-logo';
import { createDropzoneImages } from '../upload-images';
import { createDropzoneBackgrounds } from '../upload-backgrounds';
import { subscribeObservables } from '../observables/subscribe';
import {
  addEditorMenuClickListeners,
  enableOnWindowResizeListeners,
  afterCanvasReady,
  enableOnClickOutsideCanvas,
  confirmExit,
  initCursorCanvas,
  initPreviewCanvas,
  // initCloneStamp,
  // initCloneStampCanvases,
  initEraser,
  replaceImageUrlsWithImage,
} from '~core';
import { injectMagicEraserCanvasHtml } from '~core/magicEraser';
import { injectChangeClothesCanvasHtml } from '~core/changeClothes';
import { injectExpandCanvasHtml } from '~core/expand';
import { resolveLogosOnCurrentPage } from '~src/sidebar/logo';
import { initCustomControlsMagicFrame } from '~core/magicFrame';
import { setProfilePhotoToAllMagicFrames, enableTooltips } from '~src/utils';

import { FEATURES, isFeatureEnabled } from '~src/features';
import {
  initSpellCheckerClient,
  convertTextboxesToSpellCheck,
  initCustomControlsSpellCheck,
} from '~core/spellcheck';

import { getState, modifyState } from '~src/state';
import { initVideoEventListener } from '~video';
import 'fabricjs-magic-frame';
// import '@onoranzefunebricloud/fabricjs-clone-stamp';
import '~core/cloneStamp/source';
import { getTextTemplateContext } from '../utils';
import { renderCanvasJsonTemplate } from '~core/templates';
import { hideUnaccessibleFeatures } from './hideUnaccessibleFeatures';
import { initRuler } from '~core/ruler';
import { initSnapping } from '~core/snapping';
import {
  isBlackFridayActive,
  initAiBlackFridayModal,
} from '~src/sidebar/initAiBlackFridayModal';

export const loadEditor = async (options) => {
  fabric.textureSize = window.editorConfig.textureSize;
  fabric.filterBackend = fabric.initFilterBackend();
  fabric.Object.prototype.transparentCorners = false;
  fabric.Object.NUM_FRACTION_DIGITS = 4;

  modifyState('options', options);
  modifyState('isLoading.canvas', true);

  const editorMode = getState('editorMode');

  if (
    [
      EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER,
      EDITOR_MODE_ORG_TEMPLATE_MANAGER,
    ].includes(editorMode)
  ) {
    window.editorConfig.autoSave = false;
    $('#canvas-container').css('background-color', '#f0ad4e');
  }

  $('#navbar-private').hide();
  $('#navbar-canvas').show();

  // Show Black Friday promo button and initialize modal if promo is active
  if (isBlackFridayActive()) {
    $('#bf-navbar-promo-btn').css('display', 'block').removeClass('d-none');
    initAiBlackFridayModal();
  }

  await initSidebar();
  $('body').addClass('page-editor').removeClass('page-editor-loader');

  await loadFonts();
  await subscribeObservables();

  addEditorMenuClickListeners();
  hideUnaccessibleFeatures();
  initVideoEventListener();
  enableTooltips();
  initColors();
  fixTooltipIssueOnClick();
  injectSvgIcons();
  enableOnWindowResizeListeners();
  enableOnClickOutsideCanvas();
  setHeaderSummary();
  displayHeaderControls();
  startKeyboardListener();
  // initCustomControls();
  createDropzoneLogo();
  createDropzoneImages();
  createDropzoneBackgrounds();

  modifyState('initialCanvasZoom', options.zoom);
  modifyState('currentCanvasZoom', options.zoom);
  $('#canvas-zoom').css('transform', `scale(${options.zoom})`);
  $('#brush-canvas-container').css('transform', `scale(${options.zoom})`);
  $('.canvas-container').css('width', options.width);
  $('.canvas-container').css('height', options.height);

  $('#editor-menu').css('visibility', 'visible');
  $('#editor-menu-right').css('visibility', 'visible');

  $('.app-spinner').hide();
  $('#editor').show();

  // Initialize spell checker only if feature is enabled
  const allowSpellcheck = await isFeatureEnabled(FEATURES.SPELLCHECK);
  if (allowSpellcheck) {
    await initSpellCheckerClient();
  }

  window.canvas = new fabric.Canvas('canvas', {
    isDrawingMode: false,
    hoverCursor: 'pointer',
    selection: true,
    backgroundColor: '#ffffff',
    width: options.width,
    height: options.height,
    enableRetinaScaling: false,
    preserveObjectStacking: true,
    fireRightClick: true,
    fireMiddleClick: true,
    stopContextMenu: true,
  });

  initCursorCanvas({
    width: options.width,
    height: options.height,
  });

  injectMagicEraserCanvasHtml();

  const allowUseVto = await isFeatureEnabled(FEATURES.AI_USE_VTO, false);
  if (allowUseVto) {
    injectChangeClothesCanvasHtml();
  }

  const allowUseExpand = await isFeatureEnabled(FEATURES.AI_USE_EXPAND, false);
  if (allowUseExpand) {
    injectExpandCanvasHtml();
  }

  // initCloneStampCanvases({
  //   width: options.width,
  //   height: options.height,
  // });

  initPreviewCanvas({
    width: options.width,
    height: options.height,
  });

  // initCloneStamp();

  // initCloneStampCanvases({
  //   width: options.width,
  //   height: options.height,
  //   backgroundColor: '#ffffff',
  // });

  initEraser();

  // Initialize snapping system first
  initSnapping(window.canvas);

  // Initialize ruler system (which depends on snapping)
  initRuler();

  window.canvas.setWidth(options.width);
  window.canvas.setHeight(options.height);

  let pages = getState('pages', []);

  if (
    pages.length === 0 &&
    [
      EDITOR_MODE_GLOBAL_TEMPLATE_MANAGER,
      EDITOR_MODE_ORG_TEMPLATE_MANAGER,
    ].includes(editorMode)
  ) {
    await addPage(false);
  }

  const {
    servizio,
    user,
    deleganti,
    necrofori,
    card,
    serviceReservation,
  } = await getTextTemplateContext();

  try {
    const textTemplate = new TextTemplate(
      servizio,
      user,
      deleganti,
      necrofori,
      card,
      serviceReservation
    );

    let context = textTemplate.getContext();
    console.log(context);

    // Questo IF skippa il render delle variabili con Mustache
    // Per permetterci di lavorare ai templates.
    if (editorMode === EDITOR_MODE_DEFAULT && servizio) {
      for (const page of pages) {
        page.canvasJson = renderCanvasJsonTemplate(
          page.canvasJson,
          textTemplate
        );
      }
    }
  } catch (err) {
    console.error('Failed to render TextTemplate', err);
  }

  const canvasJson = getState('pages[0].canvasJson', '');

  if (canvasJson) {
    window.canvas.loadFromJSON(canvasJson, async () => {
      // Convert any loaded textboxes to SpellCheckTextbox instances only if feature is enabled
      if (allowSpellcheck) {
        convertTextboxesToSpellCheck(window.canvas);
      }

      await resolveLogosOnCurrentPage();
      modifyState('undoInProgress', false);
      window.canvas.renderAll();
      await afterCanvasReady();
      initCustomControlsMagicFrame();
      await setProfilePhotoToAllMagicFrames();
      await replaceImageUrlsWithImage();
      await generateCurrentPageThumbnail();
      addPagesToSidebar();
      modifyState('currentPageIndex', 0);
      modifyState('isLoading.canvas', false);
    });
  } else {
    window.canvas.renderAll();

    await afterCanvasReady();
    initCustomControlsMagicFrame();
    await generateCurrentPageThumbnail();
    addPagesToSidebar();
    modifyState('isLoading.canvas', false);
  }

  // Initialize spell check controls only if feature is enabled
  if (allowSpellcheck) {
    initCustomControlsSpellCheck();
  }

  // Set the initial history for undo/redo
  let initialHistory = [];
  for (let i = 0; i < pages.length; i++) {
    initialHistory[i] = [pages[i].canvasJson];
    modifyState(`currentHistoryIndex[${i}]`, 0);
  }
  modifyState('history', initialHistory);

  // TODO: check why it breaks when there is magicframe
  //displaySidebarAnimations();
  window.onbeforeunload = confirmExit;
};
